
import ctrlMixins from './_mixins.js';
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import urlUtil from '@uarmRoot/src/util-url';
import { debounce, omit, maxBy } from 'lodash';
import { ctrlParamsForAll } from './_common-stuff.ts';

/** справочник кодов обработчиков полей внешней системы */
const handlerCodes = {
	'FULL_WITH_INDEX': {
		title: 'Полный адрес с индексом',
		getter: (json) => {
			return [json.address_details.postal_code, json.full_name].filter(v => v).join(', ');
		},
	},
	'FULL_WITHOUT_INDEX': {
		title: 'Полный адрес без индекса',
		// вырезать "Город Москва, внутригородская территория (внутригородское муниципальное образование) города федерального значения муниципальный округ Раменки"? Тогда надо будет по слоям шарохаться
		getter: (json) => `${json.full_name}`,
	},
	'POST_INDEX': {
		title: 'Почтовый индекс',
		getter: (json) => `${json.address_details.postal_code || ''}`,
	},
	'REGION_ID': {
		title: 'Регион (ID/ОКАТО)',
		getter: (json) => `${json.region_code}`,
	},
	'REGION_NAME': {
		title: 'Регион (название)',
		getter: (json) => {
			const suitableLayer = layFinder(json, 2) || dummyLay();
			return `${suitableLayer.full_name_short}`;
		},
	},
	'STREET_HOUSE': {
		title: 'Улица, Дом',
		getter: (json) => {
			const suitableLayer1 = layFinder(json, 8) || dummyLay();
			const suitableLayer2 = layFinder(json, 10) || dummyLay();
			return [suitableLayer1.full_name_short, suitableLayer2.full_name_short].filter(v => v).join(', ')
		},
	},
	'REGION_SETTLEMENT': {
		title: 'Район',
		getter: (json) => {
			const suitableLayer = layFinder(json, 2) || dummyLay();
			return suitableLayer.full_name_short;
		},
	},
	'STREET_ADDRESS': {
		title: 'Адрес, начиная с улицы',
		getter: (json) => {
			// вот это буйство переменных и отдельная функция dummyLay - потому что optional chaining не настроено
			const suitableLayer1 = layFinder(json, 8) || dummyLay();
			const suitableLayer2 = layFinder(json, 10) || dummyLay();
			const suitableLayer3 = layFinder(json, 11) || dummyLay();
			return [suitableLayer1.full_name_short, suitableLayer2.full_name_short, suitableLayer3.full_name_short].filter(v => v).join(', ');
		},
	},
	'CITY_NAME': {
		title: 'Населенный пункт (название)',
		getter: (json) => {
			const suitableLayer = layFinder(json, 6) || layFinder(json, 5) || layFinder(json, 1) || dummyLay();
			return suitableLayer.full_name_short;
		},
	},
	'CITY_TYPE': {
		title: 'Населенный пункт (тип)',
		getter: (json) => {
			const suitableLayer = layFinder(json, 6) || layFinder(json, 5) || layFinder(json, 1) || dummyLay();
			return suitableLayer.type_name;
		},
	},
	'CITY_ID': {
		title: 'Населенный пункт (ID с добавлением в справочник при необх.)',
		getter: (json) => {
			const suitableLayer = layFinder(json, 6) || layFinder(json, 5) || layFinder(json, 1) || dummyLay();
			// при наполнении справочника app_city признан каноническим вариант с префиксом "г. ",
			// а в ГАР у Москвы и городов федерального значения приставка "Город ", у других городов - "г "
			// это касается только случая со справочником city_id, то есть в других
			// (текстовая колонка "имя населенного пункта" или имя НП в полном адресе) берём то, что отдает ГАР
			const fixedVal = suitableLayer.full_name_short.replace(/^Город /, 'г. ').replace(/^г /g, 'г. ');
			return fixedVal;
		},
		asyncGetterValueProcessingFn: genericAsyncGetterValueProcessingFn,
	},
	'RAW_JSON': {
		title: 'JSON из внешней системы "как есть"',
		getter: (json) => JSON.stringify(json),
	},
	'OKATO': {
		title: 'ОКАТО',
		getter: (json) => {
			let okato = String(json.address_details.okato);
			if (okato.startsWith('40')) okato = '40000000000'; // спб
			if (okato.startsWith('45')) okato = '45000000000'; // москва
			return okato;
		},
	},
}
const emptyPlaceholder = '';

// TODO избавиться от c_value и $props.value, ибо контрол не рассчитан на то чтобы он имел колонку. Чтоб не путаться
// Vue.extend нельзя! будут ошибки с $attrs и $listeners
export default {
	name: 'ctrl_dropdown_address',
	mixins: [ctrlMixins], // c_value, c_ctrlParams, c_srv_options, пропсы view_type, field_meta и так далее
	props: {
		value: {
			type: String,
			default: '',
		},
		/** Все поля данного АРМ в карточке, со значениями */
		card_meta: {
			type: Array,
			default: [],
		},
	},
	components: {
		Multiselect
	},
	meta: {
		// это наши данные, т.е. вуй при объявлении компонента их не использует
		title: 'Адрес с подсказчиком ГАР',
		params: {
			/** Соответствие имён полей АРМ и функций, которые из внешнего JSON устанавливают этих полей значения */
			armFields: {
				name: 'Поля АРМ',
				tip: '<a href="https://wiki.oooinex.ru/pages/viewpage.action?pageId=113018117">Описание в Wiki</a>',
				editType: 'addressHandlersEditor',
				paramCustomData: { handlerCodes },
			},
			...ctrlParamsForAll,
		},
	},
	data: function() {

		//const valueFromDb = this.$props.value; // c_value в data ещё не готова, т.к. это компьютед
		// если бы не парсинг (в БД строка, а разворачивать в объект умеет только UCM, UARM не умеет),
		// то можно было бы сразу v-model="c_value"
		//const selectedValue = valueFromDb || ''; // valueFromDb ? JSON.parse(valueFromDb) : '';

		return {
			connectedMetaFields: [],
			handlersSyncWorking: false,
			selectedValue: null,
			optionsList: [],
			isLoading: false,
			abortController: null,
			isJsonField: this.$props.field_meta.dbcoltype === 'jsonb',
			errText: '',
		}
	},
	methods: {
		asyncFind: debounce(function (query) {
			if (!query) return;
			const $d = this.$data;
			if ($d.abortController) $d.abortController.abort(); // отменяем предыдущий запрос
			$d.abortController = new AbortController(); // CancelToken задепрекейтили
			const garHost = urlUtil.isWizard || urlUtil.isLocalDomain ? urlUtil.remoteDomainForLPTK : '';
			// Описание АПИ тут https://fias-public-service.nalog.ru/api/spas/v2.0/swagger/index.html
			const url = `${garHost}/gar/api/spas/v2.0/SearchAddressItems?search_string=${encodeURIComponent(query)}&address_type=1`;
			$d.isLoading = true;
			axios({
				url,
				signal: $d.abortController.signal,
			}).then(res => {
				// $d.optionsList = res && res.data && res.data.hints;
				$d.optionsList = res && res.data && res.data.addresses;
			}).catch(err => {
				if (err.name !== 'CanceledError') throw err; // abortController выдаёт такие, мы делаем намеренно, так что исключение не нужно
			}).finally(() => {
				$d.isLoading = false;
			})
		}, 200),
		async onSelect(opt) {
			console.log(`Данные внешней системы:`, opt);
			const fieldsToSet = {};
			this.$data.handlersSyncWorking = true;
			this.$data.isLoading = true;
			this.$data.errText = ``;
			try {
				for (const fieldName in this.c_ctrlParams.armFields) {
					const fi = this.c_ctrlParams.armFields[fieldName];
					const handlerObj = handlerCodes[fi.handlerCode];
					const getter = handlerObj && handlerObj.getter;
					if (typeof getter !== 'function') {
						throw `Обработчик ${fi.handlerCode} не имеет геттера`;
					}
					const getterValue = getter(opt);
					// по уму надо все asyncGetterValueProcessingFn собрать в Promise.allSettled, но пока не стоит свеч
					const val2set = handlerObj.asyncGetterValueProcessingFn ? await handlerObj.asyncGetterValueProcessingFn({ getterValue, fieldName, vm: this, armFieldsItem: fi, selectedOption: opt }) : getterValue;
					fieldsToSet[fieldName] = {
						newValue: val2set,
					};
				}
			} catch (error) {
				// все равно выбрасываем ошибку, но и пишем об этом в интерфейсе
				this.$data.errText = `Произошла ошибка`;
				throw error;
			}
			this.$data.handlersSyncWorking = false;
			this.$data.isLoading = false;
			// this.c_value = JSON.stringify(omit(opt, 'full_name_html')); // поле должно быть JSON
			this.c_value = opt.full_name; // поле должно быть JSON
			this.setSearchText(opt.full_name);
			// console.log(`fieldsToSet=`, fieldsToSet);
			this.mixin_inputEvent({
				ctrlCode: this.$props.field_meta.ctrlCode,
				dbcolname: this.$props.field_meta.dbcolname,
				evtType: 'set',
				// fullValue: {id: v.id,name: v.name,REVERT_VALUE_TO_PLAIN: true},
				fieldsToSet,
			})
			this.updconnectedMetaFields();
		},
		updconnectedMetaFields() {
			const connectedMetaFields = [];
			// console.log(`this.$props.card_meta=`, this.$props.card_meta);
			const c_ctrlParams = this.$props.field_meta.ctrlParams; // имя переменной как миксин
			for (const ctrlParDbcolname in c_ctrlParams.armFields) {
				const parField = c_ctrlParams.armFields[ctrlParDbcolname];
				const metaField = this.$props.card_meta
					.find(fi => fi.dbcolname === ctrlParDbcolname);
				if (metaField) {
					// вводим кастомное клиентское свойство поля АРМ - displayValue
					// TODO проверить со статиком
					const val = metaField.value;
					const srvOption = metaField.srv_options && metaField.srv_options.find(opt => opt.id === val);
					const srvOptionName = srvOption && srvOption.name;
					metaField.displayValue = srvOptionName || (val && val.name) || val;

					connectedMetaFields.push(metaField);
				}
			}
			// console.log(`connectedMetaFields=`, connectedMetaFields); // JSON.stringify(connectedMetaFields));
			this.$data.connectedMetaFields = connectedMetaFields;
			// return connectedMetaFields;
		},
		setSearchText(text) {
			// чтобы значение поисковой строки было не тем что ввел пользователь в нее, а выбранным ранее адресом
			// знаю что зашквар напрямую менять данные, но вот
			this.$refs.MultiselectComp.$data.search = text;
		},
		clearValue() {
			this.$data.selectedValue = null;
			this.c_value = null;
		},
		manageCountryCase() {

		},
	},
	watch: {
	},
	computed: {
		/** Отображаем в контроле Адрес только скрытые */
		connectedMetaFieldsHidden() {
			return this.connectedMetaFields.filter(fi => fi.ctrlParams.isHidden);
		},
		/** Если колонка заполняемая контролом только одна, кажем её значение не в диве под полем "подсказчик ГАР, а вместо плейсхолдера, прямо в контроле */
		showSelectedValueInsideField() {
			return this.connectedMetaFieldsHidden.length === 1;
		},
	},
	created() {
		this.updconnectedMetaFields();
	},
	mounted() {
	},
}

/**
 * Стандартная функция для операции "поискать запись в АРМ и если не найдено, то создать"
 * Если будут другие операции, параметризовать (см. asyncGetterValueProcessingFnArmId) или переименовать функцию
 * @param param0
 */
async function genericAsyncGetterValueProcessingFn ({ getterValue, fieldName, vm, armFieldsItem, selectedOption }) {
	if (getterValue === emptyPlaceholder) return
	const siteId = vm.$route.params.vpId || 107;
	const armId = armFieldsItem.asyncGetterValueProcessingFnArmId || 1236; // ID АРМ справочника, в котором будет осуществляться поиск и добавление в случае ненахода, а также простановка идентификатора из этого самого АРМ. 1236 - это города из app_city
	const domain = urlUtil.getDomain();
	const armUrlStart = `${domain}${urlUtil.getPath()}${siteId}/${armId}`;

	// если в метаданных указан кастомный геттер значения ДЛЯ ПОИСКА по полю - используем его
	// да, я знаю, что название полей уже безумное, как в анекдотичном ява-коде, но по ходу лучше так :) и да, указанное asyncGetterValueProcessingFnSearchGetterHandlerCode в метаданных - очень редкий случай
	const searchValueCode = armFieldsItem.asyncGetterValueProcessingFnSearchGetterHandlerCode;
	const searchValue = searchValueCode ? handlerCodes[searchValueCode].getter(selectedOption) : getterValue;

	// если в метаданных указан кастомный геттер значения, исп. ДЛЯ СОЗДАНИЯ записи - используем его в дополнение
	// да, я знаю, что название полей уже безумное, как в анекдотичном ява-коде, но по ходу лучше так :) и да, указанное asyncGetterValueProcessingFnAddColForCreateHandlerCode в метаданных - очень редкий случай
	const addColForCreateName = armFieldsItem.asyncGetterValueProcessingFnAddColForCreateName;
	const addColForCreate = armFieldsItem.asyncGetterValueProcessingFnAddColForCreateHandlerCode;
	const addColForCreateVal = addColForCreate ? handlerCodes[addColForCreate].getter(selectedOption) : getterValue;

	/** Ищем запись в списке этого АРМ (должен иметь name в фильтре и списке! TODO автопроверка этого) */
	const searchParam = armFieldsItem.asyncGetterValueProcessingFnSearchField || 'name';
	let findDictRes = await axios.get(`${armUrlStart}/data?${searchParam}=${searchValue}`);
	const record0 = findDictRes && findDictRes.data.records[0];
	// Запись в справочнике существует
	if (record0) {
		// если найдено более 1 одного совпадения, заполнить поле в АРМ данными с max Id
		const maxRec = maxBy(findDictRes.data.records, 'primaryKeyValue');
		// @ts-expect-error
		const oldDictItemId = maxRec.primaryKeyValue;
		// @ts-expect-error
		const oldDictItemNameField = maxRec.fields.find(fi => fi.fieldId === 'name');
		const oldDictItemName = oldDictItemNameField && oldDictItemNameField.value;
		return {
			id: oldDictItemId,
			name: oldDictItemName,
		};
	}
	// Запись в справочнике не существует
	else {
		console.warn(`Значение из справочника АРМ ${armId} не найдено, добавляем ${getterValue}`);
		const fData = new FormData();
		const fields = [{ fieldId: 'name', value: getterValue }];
		// если в поиске используется другое, отличное от name поле - добавляем его в создание
		if (searchValueCode) {
			fields.push({ fieldId: searchParam, value: searchValue });
		}
		if (addColForCreate) {
			fields.push({ fieldId: addColForCreateName, value: addColForCreateVal });
		}
		fData.append('jsonPayload', JSON.stringify({ records: [{ armId, mode: 'create', fields }] }));
		// если будет больше таких полей, можно сделать неблокирующе от синхронных полей, а не await
		const addDictRes = await axios.post(`${armUrlStart}/data`, fData);
		const newDictItem = addDictRes.data.records[0];
		const newDictItemId = newDictItem.primaryKeyValue;
		const newDictItemNameField = newDictItem.fields.find(fi => fi.fieldId === 'name');
		const newDictItemName = newDictItemNameField && newDictItemNameField.value;
		console.log(`Новая запись «${newDictItemName}» создана с ID=${newDictItemId}`);

		// Добавляем в srv_options поля новую запись для видимой выпадушки
		// и чтобы в displayValue был не id, a название
		const metaField = vm.$props.card_meta.find(fi => fi.dbcolname === fieldName);
		if (metaField && metaField.srv_options) {
			metaField.srv_options.push({ id: newDictItemId, name: newDictItemName })
		}

		return {
			id: newDictItemId,
			name: newDictItemName,
		}
	}

}

function layFinder(json, level) {
	const foundLayer = json.hierarchy.find(lay => lay.object_level_id === level);
	if (!foundLayer) {
		console.log(`layFinder: Слой с object_level_id=${level} не удалось найти в json.hierarchy`, json.hierarchy);
	}
	return foundLayer
}

function dummyLay() {
	// вот это буйство переменных и отдельная функция dummyLay - потому что optional chaining не настроено
	return { full_name: emptyPlaceholder, full_name_short: emptyPlaceholder }
}

